<template>
  <!-- Global Countdown and Stop Button -->
  <Teleport to="body">
    <!-- Countdown (3, 2, 1) -->
    <Transition name="morph">
      <div v-if="isCountingDown" class="floating-countdown" key="countdown">
        <div class="countdown-circle">
          <div class="countdown-number">{{ countdownValue }}</div>
        </div>
        <p class="countdown-text">Recording starts in...</p>
        <v-btn
          variant="text"
          color="error"
          @click="cancelCountdown"
          prepend-icon="mdi-close"
          size="small"
          class="mt-2"
          aria-label="Cancel recording"
        >
          Cancel
        </v-btn>
      </div>
    </Transition>

    <!-- Stop Recording Button -->
    <Transition name="morph">
      <div v-if="isRecording" class="floating-stop-button" key="recording">
        <v-btn
          color="error"
          size="x-large"
          elevation="8"
          @click="stopRecording"
          class="stop-btn"
          aria-label="Stop recording"
        >
          <v-icon icon="mdi-stop-circle" size="32" />
          <span class="stop-text">Stop Recording</span>
          <span class="stop-duration">{{ formattedDuration }}</span>
        </v-btn>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import screenRecordingService from '@/services/screenRecordingService';

const router = useRouter();
const route = useRoute();

// Reactive state from the global service
const isCountingDown = computed(() => screenRecordingService.state.value === 'countdown');

const isRecording = computed(() => screenRecordingService.state.value === 'recording');

const countdownValue = computed(() => screenRecordingService.countdownValue.value);

const formattedDuration = computed(() => screenRecordingService.getFormattedDuration());

// Methods
const stopRecording = () => {
  screenRecordingService.stopRecording();
};

const cancelCountdown = () => {
  screenRecordingService.cancelCountdown();
};

// Watch for recording completion - navigate back to feedback page
watch(
  () => screenRecordingService.state.value,
  (newState, oldState) => {
    // When recording stops and we have a blob, navigate to feedback page
    if (
      oldState === 'recording' &&
      newState === 'stopped' &&
      screenRecordingService.recordedBlob.value &&
      route.path !== '/settings/feedback'
    ) {
      router.push('/settings/feedback');
    }
  }
);
</script>

<style scoped lang="scss">
/* Floating Countdown - Globally positioned */
.floating-countdown {
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: var(--z-index-overlay);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(var(--v-theme-surface-rgb), 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  .countdown-circle {
    width: 120px;
    height: 120px;
    margin-bottom: 16px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 32px rgba(var(--v-theme-primary-rgb), 0.4);
    animation: countdown-pulse 1s ease-in-out infinite;

    .countdown-number {
      font-size: 56px;
      font-weight: 700;
      color: white;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }

  .countdown-text {
    font-size: 14px;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0 0 8px 0;
    text-align: center;
  }
}

@keyframes countdown-pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 8px 32px rgba(var(--v-theme-primary-rgb), 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 48px rgba(var(--v-theme-primary-rgb), 0.6);
  }
}

/* Floating Stop Button - Globally positioned */
.floating-stop-button {
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: var(--z-index-overlay);

  .stop-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 16px 24px !important;
    height: auto !important;
    border-radius: 16px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
    backdrop-filter: blur(10px);

    .stop-text {
      font-size: 14px;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0;
    }

    .stop-duration {
      font-family: monospace;
      font-size: 12px;
      opacity: 0.9;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4) !important;
    }
  }
}

@keyframes float-in {
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Morph Transition - Smooth transition between countdown and stop button */
.morph-enter-active,
.morph-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.morph-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(20px);
}

.morph-leave-to {
  opacity: 0;
  transform: scale(1.2) translateY(-20px);
}

@media (max-width: 599px) {
  .floating-countdown {
    bottom: 88px; /* Above mobile bottom nav */
    right: 16px;
    left: 16px;
    padding: 20px;

    .countdown-circle {
      width: 100px;
      height: 100px;

      .countdown-number {
        font-size: 48px;
      }
    }

    .countdown-text {
      font-size: 12px;
    }
  }

  .floating-stop-button {
    bottom: 88px; /* Above mobile bottom nav */
    right: 16px;
    left: 16px;

    .stop-btn {
      width: 100%;
      flex-direction: row;
      justify-content: center;
      gap: 12px;
      padding: 20px !important;
    }
  }
}
</style>
